import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useToast } from "./use-toast";
import { jobSchema } from "@/lib/validations";

// Public job interface (from secure RPC - no employer_id exposed)
export interface PublicJob {
  id: string;
  title: string;
  job_type: string;
  location: string;
  schedule: string | null;
  description: string | null;
  hourly_rate: number | null;
  is_active: boolean;
  is_featured: boolean;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  company_name: string | null;
  employer_city: string | null;
  has_full_access?: boolean; // Indicates if user has subscription/employer access
}

// Full job interface (for employers only)
export interface Job {
  id: string;
  employer_id: string;
  title: string;
  job_type: string;
  location: string;
  schedule: string | null;
  description: string | null;
  hourly_rate: number | null;
  is_active: boolean;
  is_featured: boolean;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  applications_count?: number;
}

interface CreateJobInput {
  title: string;
  job_type: string;
  location: string;
  schedule?: string;
  description?: string;
  hourly_rate?: number;
}

interface UpdateJobInput extends Partial<CreateJobInput> {
  id: string;
  is_active?: boolean;
}

export const useJobs = (filters?: { job_type?: string; location?: string; search?: string; job_category?: string }) => {
  return useQuery({
    queryKey: ["jobs", filters],
    queryFn: async () => {
      // Use secure RPC that enforces subscription-based access
      const safeSearch = filters?.search
        ? filters.search.trim().slice(0, 100)
        : null;

      const { data, error } = await supabase.rpc("get_jobs_with_subscription_check", {
        job_type_filter: filters?.job_type === "all" ? null : (filters?.job_type || null),
        location_filter: filters?.location === "all" ? null : (filters?.location || null),
        search_term: safeSearch,
        job_category: filters?.job_category || null,
      });

      if (error) throw error;

      return (data || []) as PublicJob[];
    },
  });
};

export const useJobById = (id: string | undefined) => {
  return useQuery({
    queryKey: ["job", id],
    queryFn: async () => {
      if (!id) return null;

      // Use secure RPC that enforces subscription-based access
      const { data, error } = await supabase.rpc("get_job_by_id_secure", {
        p_job_id: id,
      });

      if (error) throw error;
      if (!data || data.length === 0) return null;

      return data[0] as PublicJob;
    },
    enabled: !!id,
  });
};

export const useEmployerJobs = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["employer-jobs", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data: jobs, error } = await supabase
        .from("jobs")
        .select("*")
        .eq("employer_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Get application counts for each job
      const jobsWithCounts = await Promise.all(
        (jobs || []).map(async (job) => {
          const { count } = await supabase
            .from("applications")
            .select("*", { count: "exact", head: true })
            .eq("job_id", job.id);

          return { ...job, applications_count: count || 0 };
        })
      );

      return jobsWithCounts as Job[];
    },
    enabled: !!user?.id,
  });
};

// Helper function to geocode with retry
async function geocodeJobWithRetry(
  jobId: string,
  city: string,
  maxRetries = 2
): Promise<boolean> {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`[Geocode] Attempt ${attempt}/${maxRetries} for job ${jobId}`);
      
      const { data, error } = await supabase.functions.invoke("geocode-job", {
        body: { job_id: jobId, city },
      });

      if (error) {
        console.warn(`[Geocode] Attempt ${attempt} failed:`, error);
        if (attempt === maxRetries) return false;
        // Wait before retry
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        continue;
      }

      if (data?.success) {
        console.log(`[Geocode] Success for job ${jobId}:`, data);
        return true;
      }

      console.warn(`[Geocode] Attempt ${attempt} returned error:`, data?.error);
      if (attempt === maxRetries) return false;
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
    } catch (err) {
      console.error(`[Geocode] Attempt ${attempt} exception:`, err);
      if (attempt === maxRetries) return false;
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
    }
  }
  return false;
}

export const useCreateJob = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: CreateJobInput) => {
      if (!user?.id) throw new Error("Non authentifié");

      // Validate input
      const validation = jobSchema.safeParse(input);
      if (!validation.success) {
        throw new Error(validation.error.errors[0]?.message || "Données invalides");
      }

      const validData = validation.data;

      const { data, error } = await supabase
        .from("jobs")
        .insert({
          employer_id: user.id,
          title: validData.title,
          job_type: validData.job_type,
          location: validData.location,
          schedule: validData.schedule,
          description: validData.description,
          hourly_rate: input.hourly_rate,
          geocode_status: "pending",
        })
        .select()
        .single();

      if (error) throw error;

      // Trigger geocoding with retry - await it to ensure it completes
      geocodeJobWithRetry(data.id, validData.location)
        .then((success) => {
          if (success) {
            // Refresh map data on success
            queryClient.invalidateQueries({ queryKey: ["map-jobs"] });
            queryClient.invalidateQueries({ queryKey: ["job-for-map"] });
          } else {
            console.warn(`[Job] Geocoding failed after retries for job ${data.id}`);
          }
        });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employer-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      toast({
        title: "Offre publiée",
        description: "Votre offre d'emploi est maintenant visible.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erreur",
        description: error.message || "Impossible de publier l'offre.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateJob = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateJobInput) => {
      // If location is being updated, reset geocode status
      const updateData: Record<string, unknown> = { ...input };
      if (input.location) {
        updateData.geocode_status = "pending";
        updateData.lat = null;
        updateData.lng = null;
      }

      const { data, error } = await supabase
        .from("jobs")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      // If location was updated, trigger geocoding
      if (input.location) {
        geocodeJobWithRetry(id, input.location)
          .then((success) => {
            if (success) {
              queryClient.invalidateQueries({ queryKey: ["map-jobs"] });
              queryClient.invalidateQueries({ queryKey: ["job-for-map"] });
            }
          });
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employer-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      toast({
        title: "Offre mise à jour",
        description: "Les modifications ont été enregistrées.",
      });
    },
  });
};

export const useDeleteJob = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("jobs").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employer-jobs"] });
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      toast({
        title: "Offre supprimée",
        description: "L'offre a été retirée de la plateforme.",
      });
    },
  });
};
